import { useState } from "react";
import MicroLabel from "@/components/base/MicroLabel";

type OrderType = "market" | "limit" | "stop";

interface Values {
  lots: number;
  spread: number;
  commission: number;
  slippage: number;
  nights: number;
}

const DEFAULTS: Values = {
  lots: 1,
  spread: 1,
  commission: 7,
  slippage: 0.5,
  nights: 1,
};

/* EUR/USD: one standard lot is worth about $10 per pip. */
const PIP_VALUE_USD = 10;
/* Illustrative overnight (swap) debit per standard lot, per night. */
const SWAP_PER_LOT_NIGHT = 3.2;
const ACCOUNT_SIZE = 10000;

/* How each order type exposes you to slippage, relative to the base figure.
   Limit orders can never fill worse than your price, so they do not slip;
   a stop becomes a market order the instant it triggers, so it slips hardest. */
const SLIP_MULT: Record<OrderType, number> = {
  market: 1,
  limit: 0,
  stop: 1.6,
};

const ORDER_TYPES: { key: OrderType; label: string; caption: string }[] = [
  { key: "market", label: "Market", caption: "Fills now at the next available price — full slippage." },
  { key: "limit", label: "Limit", caption: "Fills only at your price or better — no slippage at all." },
  { key: "stop", label: "Stop", caption: "Triggers then goes to market — slips hardest, ×1.6." },
];

const THRESHOLDS = [0.1, 0.25, 0.5];

interface Control {
  key: keyof Values;
  label: string;
  display: string;
  min: number;
  max: number;
  step: number;
  minLabel: string;
  maxLabel: string;
  disabled?: boolean;
}

const money = (value: number): string => `$${value.toFixed(2)}`;

export default function CostCalculator() {
  const [values, setValues] = useState<Values>(DEFAULTS);
  const [orderType, setOrderType] = useState<OrderType>("market");
  const [threshold, setThreshold] = useState(0.5);

  const slipPips = values.slippage * SLIP_MULT[orderType];

  const spreadCost = values.spread * PIP_VALUE_USD * values.lots;
  const commissionCost = values.commission * values.lots;
  const slippageCost = slipPips * PIP_VALUE_USD * values.lots;
  const swapCost = values.nights * SWAP_PER_LOT_NIGHT * values.lots;
  const total = spreadCost + commissionCost + slippageCost + swapCost;

  const accountPctNum = (total / ACCOUNT_SIZE) * 100;
  const accountPct = accountPctNum.toFixed(2);
  /* Compare at the precision that is actually displayed, so the banner can
     never read "0.25% of the account — above your 0.25% limit". */
  const overThreshold = Number(accountPct) > threshold;

  const activeType = ORDER_TYPES.find((item) => item.key === orderType) ?? ORDER_TYPES[0];

  const slipDisplay =
    orderType === "limit"
      ? "0 pips · locked"
      : orderType === "stop"
        ? `${values.slippage.toFixed(1)} × 1.6 = ${slipPips.toFixed(1)} pips`
        : `${values.slippage.toFixed(1)} pips`;

  const controls: Control[] = [
    {
      key: "lots",
      label: "Position size",
      display: `${values.lots.toFixed(1)} lots`,
      min: 0.1,
      max: 5,
      step: 0.1,
      minLabel: "0.1",
      maxLabel: "5",
    },
    {
      key: "spread",
      label: "Spread",
      display: `${values.spread.toFixed(1)} pips`,
      min: 0,
      max: 5,
      step: 0.1,
      minLabel: "0",
      maxLabel: "5",
    },
    {
      key: "commission",
      label: "Commission (round-turn)",
      display: `$${Number(values.commission.toFixed(1))} / lot`,
      min: 0,
      max: 20,
      step: 0.5,
      minLabel: "$0",
      maxLabel: "$20",
    },
    {
      key: "slippage",
      label: "Slippage (base)",
      display: slipDisplay,
      min: 0,
      max: 10,
      step: 0.1,
      minLabel: "0",
      maxLabel: "10",
      disabled: orderType === "limit",
    },
    {
      key: "nights",
      label: "Nights held",
      display: `${values.nights} ${values.nights === 1 ? "night" : "nights"}`,
      min: 0,
      max: 10,
      step: 1,
      minLabel: "0",
      maxLabel: "10",
    },
  ];

  const breakdown = [
    { label: "Spread", value: spreadCost, dot: "bg-primary-500" },
    { label: "Commission", value: commissionCost, dot: "bg-secondary-500" },
    { label: "Slippage", value: slippageCost, dot: "bg-accent-500" },
    { label: "Swap", value: swapCost, dot: "bg-background-400" },
  ];

  const update = (key: keyof Values, raw: string) => {
    setValues((prev) => ({ ...prev, [key]: Number(raw) }));
  };

  return (
    <div className="border-2 border-primary-500 bg-foreground-950">
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b-2 border-primary-500">
        <MicroLabel tone="onDarkPrimary">Execution cost calculator · EUR/USD</MicroLabel>
        <button
          type="button"
          onClick={() => {
            setValues(DEFAULTS);
            setOrderType("market");
            setThreshold(0.5);
          }}
          className="group inline-flex items-center gap-2 px-3 py-1.5 border-2 border-background-50/25 hover:border-accent-500 transition-colors cursor-pointer"
        >
          <i className="ri-restart-line text-background-300 transition-colors group-hover:text-accent-400"></i>
          <MicroLabel
            tone="onDarkSoft"
            className="transition-colors group-hover:text-accent-400"
          >
            Reset
          </MicroLabel>
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12">
        {/* Controls */}
        <div className="lg:col-span-7 p-5 md:p-6 border-b-2 lg:border-b-0 lg:border-r-2 border-primary-500/40">
          {/* Order type */}
          <MicroLabel tone="onDarkMuted">Order type</MicroLabel>
          <div className="mt-3 grid grid-cols-3 gap-1.5">
            {ORDER_TYPES.map((item) => {
              const active = item.key === orderType;
              return (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => setOrderType(item.key)}
                  aria-pressed={active}
                  className={`px-2 py-2 border-2 font-label text-[11px] uppercase tracking-[0.12em] font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                    active
                      ? "bg-primary-500 border-primary-500 text-foreground-950"
                      : "border-background-50/25 text-background-300 hover:border-primary-500 hover:text-primary-500"
                  }`}
                >
                  {item.label}
                </button>
              );
            })}
          </div>
          <p className="mt-2 text-xs leading-relaxed text-background-400">{activeType.caption}</p>

          <div className="mt-6">
            <MicroLabel tone="onDarkMuted">Drag to model one trade</MicroLabel>
          </div>
          <div className="mt-5 flex flex-col gap-6">
            {controls.map((control) => (
              <div key={control.key}>
                <div className="flex items-center justify-between gap-3">
                  <label htmlFor={`cost-${control.key}`} className="block">
                    <MicroLabel tone={control.disabled ? "onDarkMuted" : "onDarkSoft"}>
                      {control.label}
                    </MicroLabel>
                  </label>
                  <span
                    className={`font-label text-sm font-semibold tabular-nums whitespace-nowrap ${
                      control.disabled ? "text-background-500" : "text-primary-400"
                    }`}
                  >
                    {control.display}
                  </span>
                </div>
                <input
                  id={`cost-${control.key}`}
                  type="range"
                  min={control.min}
                  max={control.max}
                  step={control.step}
                  value={values[control.key]}
                  disabled={control.disabled}
                  onChange={(event) => update(control.key, event.target.value)}
                  className={`mt-3 w-full accent-primary-500 ${
                    control.disabled ? "cursor-not-allowed opacity-40" : "cursor-pointer"
                  }`}
                />
                <div className="mt-2 flex justify-between">
                  <MicroLabel tone="onDarkDim">{control.minLabel}</MicroLabel>
                  <MicroLabel tone="onDarkDim">{control.maxLabel}</MicroLabel>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-6 border-t-2 border-background-50/15 pt-4">
            <MicroLabel mark="dot" tone="onDarkDim">
              Cost basis
            </MicroLabel>
            <p className="mt-2 text-xs leading-relaxed text-background-400">
              Spread, slippage and swap are priced off a $10 / pip standard lot. Illustrative
              figures, not a live quote.
            </p>
          </div>
        </div>

        {/* Result */}
        <div className="lg:col-span-5 p-5 md:p-6 flex flex-col bg-foreground-900">
          <MicroLabel tone="onDarkMuted">Total execution cost</MicroLabel>
          <p className="mt-2 font-heading font-black leading-none text-background-50 text-4xl md:text-5xl">
            {money(total)}
          </p>
          <MicroLabel tone="onDarkMuted" className="mt-2">
            = {accountPct}% of a $10,000 account
          </MicroLabel>

          {/* Warning threshold */}
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <MicroLabel tone="onDarkDim">Warn above</MicroLabel>
            <div className="flex items-center border-2 border-background-50/25">
              {THRESHOLDS.map((item) => {
                const active = item === threshold;
                return (
                  <button
                    key={item}
                    type="button"
                    onClick={() => setThreshold(item)}
                    aria-pressed={active}
                    className={`px-2.5 py-1 font-label text-[10px] font-semibold tracking-[0.08em] whitespace-nowrap transition-colors cursor-pointer ${
                      active
                        ? "bg-secondary-500 text-background-50"
                        : "text-background-400 hover:text-secondary-300"
                    }`}
                  >
                    {item.toFixed(2)}%
                  </button>
                );
              })}
            </div>
          </div>

          {/* Stacked proportion bar */}
          <div className="mt-6 flex h-3 border-2 border-background-50/20 overflow-hidden">
            {total > 0 &&
              breakdown.map((row) => (
                <span
                  key={row.label}
                  className={`${row.dot} transition-all duration-300`}
                  style={{ width: `${(row.value / total) * 100}%` }}
                ></span>
              ))}
          </div>

          {/* Breakdown */}
          <div className="mt-5 flex flex-col gap-3">
            {breakdown.map((row) => {
              const pct = total > 0 ? (row.value / total) * 100 : 0;
              return (
                <div key={row.label}>
                  <div className="flex items-center justify-between gap-3">
                    <MicroLabel tone="onDarkSoft">
                      <span className={`w-2.5 h-2.5 shrink-0 ${row.dot}`}></span>
                      {row.label}
                    </MicroLabel>
                    <span className="font-label text-sm tabular-nums text-background-50">
                      {money(row.value)}
                      <span className="ml-2 text-[11px] text-background-500">{pct.toFixed(0)}%</span>
                    </span>
                  </div>
                  <div className="mt-1.5 h-1 bg-background-50/10">
                    <div
                      className={`h-full ${row.dot} transition-all duration-300`}
                      style={{ width: `${pct}%` }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>

          <p className="mt-auto pt-6 text-xs leading-relaxed text-background-400">
            {orderType === "limit" ? (
              <>
                A limit order shows zero slippage because it can never fill worse than your level.
                It can also sit unfilled — or fill only partly — and the total above cannot show
                that.
              </>
            ) : (
              <>
                Spread and commission are both known before you click.{" "}
                <span className="text-accent-400">Slippage</span> is not — switch the order type and
                watch it appear, vanish or spike.
              </>
            )}
          </p>
        </div>
      </div>

      {/* Cost-share warning */}
      <div
        className={`flex items-start gap-3 px-4 md:px-6 py-3 border-t-2 ${
          overThreshold
            ? "border-accent-500 bg-accent-500/15"
            : "border-primary-500 bg-primary-500/10"
        }`}
      >
        <div className="w-full">
          <MicroLabel
            mark={overThreshold ? "warn" : "check"}
            tone={overThreshold ? "onDarkAccent" : "onDarkPrimary"}
          >
            {overThreshold ? "Over budget" : "Within budget"}
          </MicroLabel>
          <p
            className={`mt-2 text-xs md:text-sm leading-relaxed ${
              overThreshold ? "text-accent-300" : "text-primary-500"
            }`}
          >
            {overThreshold
              ? `This trade costs ${accountPct}% of the account — above your ${threshold.toFixed(2)}% limit.`
              : `${accountPct}% of the account — under your ${threshold.toFixed(2)}% limit.`}
          </p>
        </div>
      </div>
    </div>
  );
}